import { inject, Service, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { ExternalFormsService } from '@superlikers/microsites-api'

import { User } from '@core/data/models'
import { LocalStorage } from '../local-storage/local-storage'

interface CacheItem<T> {
  data: T
  expiryTimestamp: number
  serverTimestamp: number
}

@Service()
export class CacheService {
  #externalFormsService = inject(ExternalFormsService)
  #localStorage = inject(LocalStorage)

  readonly #TTL_MINUTES = 720

  #serverTimestamp = signal<number | null>(null)
  #hasInitialized = signal(false)

  /** Se llama una vez tras autenticar. `AuthService` ya lo hace. Idempotente. */
  async init(currentUser: User | null): Promise<void> {
    if (this.#hasInitialized()) return
    this.#hasInitialized.set(true)

    const cachedUser = this.#localStorage.getItem('cached_user')
    if (currentUser && cachedUser !== currentUser.uid) {
      this.clear()
      this.#localStorage.setItem('cached_user', currentUser.uid)
    }

    const newTimestamp = await this.#getServerTimestamp()
    if (!newTimestamp) return

    const storedTimestamp = this.#localStorage.getItem('cache_server_timestamp')
    if (storedTimestamp && newTimestamp > parseInt(storedTimestamp, 10)) this.clear()

    this.#localStorage.setItem('cache_server_timestamp', newTimestamp.toString())
    this.#serverTimestamp.set(newTimestamp)
  }

  /** Claves semánticas: `prizes:page:1`, `ranking:general`. Nunca hashes. */
  setItem<T>(key: string, data: T): void {
    const serverTimestamp = this.#serverTimestamp()
    if (!serverTimestamp) return

    const cacheItem: CacheItem<T> = {
      data,
      expiryTimestamp: Date.now() + this.#TTL_MINUTES * 60 * 1000,
      serverTimestamp
    }

    this.#localStorage.setItem(key, JSON.stringify(cacheItem))
  }

  /** `null` si no existe, si expiró o si el servidor invalidó la caché. */
  getItem<T>(key: string): T | null {
    const item = this.#localStorage.getItem(key)
    if (!item) return null

    try {
      const cacheItem = JSON.parse(item) as CacheItem<T>
      const currentServerTime = this.#serverTimestamp()
      if (!currentServerTime) return null

      if (currentServerTime > cacheItem.serverTimestamp || Date.now() > cacheItem.expiryTimestamp) {
        this.removeItem(key)
        return null
      }

      return cacheItem.data
    } catch {
      this.removeItem(key)
      return null
    }
  }

  removeItem(key: string): void {
    this.#localStorage.removeItem(key)
  }

  clear(): void {
    this.#localStorage.clear()
  }

  async #getServerTimestamp(): Promise<number | null> {
    try {
      const list = await firstValueFrom(this.#externalFormsService.listForms({ page: 1, category: 'cache' }))
      if (list.data.length === 0) return null

      const fields = await firstValueFrom(this.#externalFormsService.getFormFields(list.data[0]._id))
      const value = fields.object.fields.find(field => field.name === 'time')?.default_value ?? ''

      const timestamp = new Date(value).getTime()
      return Number.isNaN(timestamp) ? null : timestamp
    } catch (error) {
      console.error('No se pudo leer la marca de caché del servidor:', error)
      return null
    }
  }
}
